import React from 'react';
import { cn } from '@gokrew/ui/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { SizeMap, VariationMap } from '../ui.types';
import type { ButtonProps, IconButtonProps } from './Button.model';
import styles from './Button.module.css';

export function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  children,
  className = '',
  IconSuffix = null,
  IconPrefix = null,
  onClick,
  ...props
}: ButtonProps) {
  const disabledClasses = styles.btnDisabled;

  const variants: VariationMap<string> = {
    primary: styles.btnPrimary,
    secondary: styles.btnSecondary,
    outline: styles.btnOutline,
    destructive: styles.btnDestructive,
  };

  const sizes: SizeMap<string> = {
    xs: styles.btnXs,
    sm: styles.btnSm,
    md: styles.btnMd,
    lg: styles.btnLg,
  };

  const iconSizes: SizeMap<number> = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      className={cn(
        styles.btnBase,
        sizes[size],
        disabled ? disabledClasses : variants[variant],
        className
      )}
      {...props}
    >
      {IconPrefix && <IconPrefix size={iconSizes[size]} />}
      {children}
      {IconSuffix && <IconSuffix size={iconSizes[size]} />}
    </button>
  );
}

/**
 * A button that contains only an icon.
 *
 * @prop {LucideIcon | null} Icon - The icon to display. If null, the component will not render.
 * @prop {Size} size - The size of the icon. One of "sm", "md", or "lg". Defaults to "md".
 * @prop {string} className - Additional CSS classes to apply to the button.
 * @prop {...React.ComponentProps<LucideIcon>} - Additional props to pass to the icon component.
 * @returns A button containing an icon.
 */
export function IconButton({
  Icon = null,
  size = 'md',
  className = '',
  disabled = false,
  onClick,
  ...props
}: React.ComponentProps<LucideIcon> & IconButtonProps) {
  if (!Icon) return null;

  const iconSizes: SizeMap<number> = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      className={cn(
        styles.btnBase,
        'p-2',
        disabled
          ? styles.iconBtnDisabled
          : 'hover:bg-[#F3FAFF] text-[#013249]',
        className
      )}
    >
      <Icon {...props} size={iconSizes[size]} />
    </button>
  );
}
